/**
 * Connection codes: 10 symbols from a 32-symbol alphabet (Crockford base32 without I, L, O, U),
 * roughly 50 bits of entropy. Generation happens on the server with a CSPRNG; this module only
 * holds the shared alphabet plus normalization and formatting helpers.
 */
export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 10;
/** Display grouping, e.g. `K7Q-M2PX-9RD`. */
export const CODE_GROUPS = [3, 4, 3] as const;

const LOOKALIKES: Readonly<Record<string, string>> = { O: '0', I: '1', L: '1' };
const SEPARATORS = /[\s\-_.‐-―−]/g;

/**
 * Uppercases, removes separators and maps look-alike letters (O→0, I/L→1).
 * Characters outside the alphabet are kept so that validation fails loudly.
 */
export function normalizeCode(input: string): string {
  let out = '';
  for (const ch of input.normalize('NFKC').toUpperCase().replace(SEPARATORS, '')) {
    out += LOOKALIKES[ch] ?? ch;
  }
  return out;
}

export function isValidCode(normalized: string): boolean {
  if (normalized.length !== CODE_LENGTH) return false;
  for (const ch of normalized) {
    if (!CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}

/** Groups up to 10 symbols as `XXX-XXXX-XXX`; partial input is grouped as far as it goes. */
export function formatCode(symbols: string): string {
  const groups: string[] = [];
  let offset = 0;
  for (const size of CODE_GROUPS) {
    if (offset >= symbols.length) break;
    groups.push(symbols.slice(offset, offset + size));
    offset += size;
  }
  return groups.join('-');
}

/** For live typing: keeps only valid symbols (after look-alike mapping), max 10, grouped. */
export function sanitizeCodeInput(input: string): string {
  let symbols = '';
  for (const ch of normalizeCode(input)) {
    if (CODE_ALPHABET.includes(ch)) symbols += ch;
    if (symbols.length === CODE_LENGTH) break;
  }
  return formatCode(symbols);
}
